import type { ReactNode } from 'react';
import { Info, Plus, Upload } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

import { ACCEPTED_LABEL, MAX_FILE_SIZE_MB } from '../../lib/uploadConstraints';
import { UserMenu } from './UserMenu';

interface Props {
  mobileMenuSlot?: ReactNode;
  onCreateCollection: () => void;
  onUploadClick: () => void;
  uploadDisabled: boolean;
  onHome: () => void;
}

/** Barra superior: ações globais (nova coleção, enviar arquivo) + menu do usuário. */
export function TopBar({
  mobileMenuSlot,
  onCreateCollection,
  onUploadClick,
  uploadDisabled,
  onHome,
}: Props) {
  const uploadButton = (
    <Button size="sm" variant="outline" disabled={uploadDisabled} onClick={onUploadClick}>
      <Upload className="mr-1.5 size-4" />
      Enviar arquivo
    </Button>
  );

  return (
    <header className="flex items-center gap-3 border-b px-4 py-3">
      {mobileMenuSlot}
      {/* O título é o caminho de volta à tela de seleção — antes só recarregar
          a página tirava o usuário de dentro de uma coleção. */}
      <h1 className="text-sm font-semibold">
        <button
          type="button"
          onClick={onHome}
          className="hover:text-foreground/70 cursor-pointer transition-colors"
          title="Voltar para a seleção de coleções"
        >
          Gerenciador de Coleções
        </button>
      </h1>

      <div className="ml-auto flex items-center gap-2">
        <Button size="sm" variant="ghost" onClick={onCreateCollection}>
          <Plus className="mr-1.5 size-4" />
          Nova coleção
        </Button>

        <TooltipProvider>
          {uploadDisabled ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <span>{uploadButton}</span>
              </TooltipTrigger>
              <TooltipContent>Selecione uma coleção primeiro</TooltipContent>
            </Tooltip>
          ) : (
            uploadButton
          )}

          {/* Quais formatos entram e até que tamanho: a tela não dizia em lugar
              nenhum, e o usuário só descobria pela mensagem de recusa. */}
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                className="text-muted-foreground hover:text-foreground cursor-help transition-colors"
                aria-label="Formatos aceitos no envio"
              >
                <Info className="size-4" />
              </button>
            </TooltipTrigger>
            <TooltipContent>
              Aceita {ACCEPTED_LABEL}, até {MAX_FILE_SIZE_MB} MB por arquivo.
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        <UserMenu />
      </div>
    </header>
  );
}
